import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { toast } from "react-hot-toast";
import { Shield, RotateCcw, Save, Loader2, BookmarkPlus, Bookmark, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const ROLE_LABELS = {
  cashier: "كاشير",
  accountant: "محاسب",
  employee: "موظف عام",
  viewer: "مشاهد فقط",
};

export default function EmployeePermissionsDialog({ employee, token, open, onClose, onSaved }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");

  const fetchTemplates = () => {
    axios.get(`${API}/sub-accounts/permission-templates`, { headers })
      .then((r) => setTemplates(r.data || []))
      .catch(() => {});
  };

  useEffect(() => {
    if (!open || !employee?.id) return;
    let active = true;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/sub-accounts/permissions/catalog`, { headers }),
      axios.get(`${API}/sub-accounts/${employee.id}/permissions`, { headers }),
      axios.get(`${API}/sub-accounts/permission-templates`, { headers }),
    ])
      .then(([c, d, t]) => {
        if (!active) return;
        setCatalog(c.data.catalog);
        setData(d.data);
        setSelected(new Set(d.data.effective_permissions));
        setTemplates(t.data || []);
      })
      .catch(() => toast.error("فشل تحميل الصلاحيات"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, employee?.id]);

  const applyTemplate = (tpl) => {
    setSelected(new Set(tpl.permissions));
    toast.success(`📋 تم تطبيق قالب: ${tpl.name}`);
  };

  const saveAsTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) return toast.error("اسم القالب مطلوب");
    try {
      const r = await axios.post(`${API}/sub-accounts/permission-templates`, {
        name,
        permissions: Array.from(selected),
      }, { headers });
      setTemplates((prev) => [r.data, ...prev]);
      setNewTemplateName("");
      setShowSaveTemplate(false);
      toast.success("✅ تم حفظ القالب");
    } catch {
      toast.error("فشل حفظ القالب");
    }
  };

  const deleteTemplate = async (tplId, name) => {
    if (!window.confirm(`حذف القالب "${name}"؟`)) return;
    try {
      await axios.delete(`${API}/sub-accounts/permission-templates/${tplId}`, { headers });
      setTemplates((prev) => prev.filter((t) => t.id !== tplId));
      toast.success("🗑️ تم حذف القالب");
    } catch {
      toast.error("فشل حذف القالب");
    }
  };

  const togglePerm = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleGroup = (groupKey) => {
    const groupPerms = catalog[groupKey].permissions.map((p) => p.key);
    const allSelected = groupPerms.every((p) => selected.has(p));
    setSelected((prev) => {
      const next = new Set(prev);
      groupPerms.forEach((p) => {
        if (allSelected) next.delete(p); else next.add(p);
      });
      return next;
    });
  };

  const resetToRoleDefaults = async () => {
    if (!window.confirm(`الرجوع لصلاحيات دور (${ROLE_LABELS[data?.role] || data?.role}) الافتراضية؟`)) return;
    setSaving(true);
    try {
      await axios.put(`${API}/sub-accounts/${employee.id}/permissions`, { permissions: [] }, { headers });
      toast.success("✅ تم الرجوع للصلاحيات الافتراضية");
      onSaved && onSaved();
      onClose();
    } catch {
      toast.error("فشل التعديل");
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const perms = Array.from(selected);
      await axios.put(`${API}/sub-accounts/${employee.id}/permissions`, { permissions: perms }, { headers });
      toast.success("✅ تم حفظ الصلاحيات المخصصة");
      onSaved && onSaved();
      onClose();
    } catch {
      toast.error("فشل حفظ الصلاحيات");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-cyan-400 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            صلاحيات: {employee?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
          </div>
        ) : data && catalog ? (
          <div className="space-y-4">
            {/* Status banner */}
            <div className={`p-3 rounded-lg border flex items-center justify-between ${
              data.is_custom
                ? "bg-amber-900/20 border-amber-700/40 text-amber-300"
                : "bg-emerald-900/20 border-emerald-700/40 text-emerald-300"
            }`}>
              <div>
                <p className="font-semibold text-sm">
                  {data.is_custom ? "🎯 صلاحيات مخصصة نشطة" : `📋 صلاحيات افتراضية للدور: ${ROLE_LABELS[data.role] || data.role}`}
                </p>
                <p className="text-xs opacity-80 mt-0.5">
                  {selected.size} من {data.all_permissions.length} صلاحية مفعّلة
                </p>
              </div>
              {data.is_custom && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resetToRoleDefaults}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  data-testid="reset-permissions-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5 me-1" />
                  رجوع للافتراضي
                </Button>
              )}
            </div>

            {/* ✨ قوالب الصلاحيات */}
            <div className="bg-slate-900/40 rounded-lg border border-slate-700 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
                  <Bookmark className="w-3.5 h-3.5 text-purple-400" />
                  قوالب جاهزة ({templates.length})
                </h4>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowSaveTemplate((v) => !v)}
                  className="text-purple-300 h-7 text-xs"
                  data-testid="show-save-template-btn"
                >
                  <BookmarkPlus className="w-3.5 h-3.5 me-1" />
                  حفظ التحديد الحالي كقالب
                </Button>
              </div>

              {showSaveTemplate && (
                <div className="flex gap-2 items-center bg-slate-800/60 p-2 rounded">
                  <Input
                    placeholder="اسم القالب (مثلاً: كاشير VIP)"
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    className="bg-slate-900 border-slate-600 text-white text-sm h-9"
                    data-testid="new-template-name"
                  />
                  <Button
                    size="sm"
                    onClick={saveAsTemplate}
                    className="bg-purple-600 hover:bg-purple-700 h-9"
                    data-testid="save-template-confirm-btn"
                  >
                    حفظ
                  </Button>
                </div>
              )}

              {templates.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((tpl) => (
                    <div
                      key={tpl.id}
                      className="group flex items-center gap-1 bg-purple-900/30 hover:bg-purple-900/50 border border-purple-700/40 rounded-full px-2.5 py-1 cursor-pointer transition-colors"
                      onClick={() => applyTemplate(tpl)}
                      data-testid={`template-${tpl.id}`}
                    >
                      <span className="text-xs text-purple-200">{tpl.name}</span>
                      <span className="text-[10px] text-purple-400">({tpl.permissions.length})</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl.id, tpl.name); }}
                        className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity"
                        title="حذف القالب"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {templates.length === 0 && !showSaveTemplate && (
                <p className="text-[11px] text-slate-500 text-center py-1">
                  اضبط الصلاحيات أدناه ثم اضغط "حفظ التحديد الحالي كقالب" للاستخدام مع موظفين آخرين
                </p>
              )}
            </div>

            {/* Groups */}
            {Object.entries(catalog).map(([groupKey, group]) => {
              const groupAllChecked = group.permissions.every((p) => selected.has(p.key));
              const groupSomeChecked = group.permissions.some((p) => selected.has(p.key));
              return (
                <div key={groupKey} className="bg-slate-900/50 rounded-lg border border-slate-700 overflow-hidden">
                  <div
                    className="px-4 py-2.5 bg-slate-800/60 border-b border-slate-700 flex items-center justify-between cursor-pointer hover:bg-slate-800"
                    onClick={() => toggleGroup(groupKey)}
                  >
                    <h3 className="font-semibold text-cyan-300">{group.label}</h3>
                    <div className="flex items-center gap-2 text-xs">
                      <span className={groupAllChecked ? "text-emerald-400" : groupSomeChecked ? "text-amber-400" : "text-slate-500"}>
                        {group.permissions.filter((p) => selected.has(p.key)).length} / {group.permissions.length}
                      </span>
                      <Checkbox
                        checked={groupAllChecked}
                        onCheckedChange={() => toggleGroup(groupKey)}
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`group-${groupKey}-checkbox`}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3">
                    {group.permissions.map((p) => (
                      <label
                        key={p.key}
                        className="flex items-center gap-2.5 p-2 rounded hover:bg-slate-800/50 cursor-pointer transition-colors"
                      >
                        <Checkbox
                          checked={selected.has(p.key)}
                          onCheckedChange={() => togglePerm(p.key)}
                          data-testid={`perm-${p.key}`}
                        />
                        <span className="text-sm text-slate-300">{p.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300" disabled={saving}>
            إلغاء
          </Button>
          <Button onClick={save} disabled={loading || saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-permissions-btn">
            {saving ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Save className="w-4 h-4 me-2" />}
            حفظ الصلاحيات المخصصة
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
